import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api-fetch";
import {
  Thermometer,
  RotateCcw,
  Package,
  Users,
  Search,
  Filter,
  ArrowUpDown,
  FolderTree,
  Plus,
  Droplets,
} from "lucide-react";

type TemperatureRecord = {
  id: number;
  windrow_id: number;
  windrow_name: string;
  windrow_row_id: string;
  temperature: number;
  probe_location: string | null;
  notes: string | null;
  recorded_by: string | null;
  recorded_at: string;
};

type TurnRecord = {
  id: number;
  windrow_id: number;
  windrow_name: string;
  windrow_row_id: string;
  turn_number: number;
  water_added: boolean;
  equipment: string | null;
  operator: string | null;
  notes: string | null;
  turned_at: string;
};

type MaterialRecord = {
  id: number;
  windrow_id: number;
  windrow_name: string;
  windrow_row_id: string;
  material_name: string;
  category_id: number | null;
  cubic_yards: number | null;
  weight: number | null;
  notes: string | null;
  added_at: string;
};

type Operator = {
  id: number;
  name: string;
  email: string;
  role: string;
  last_active: string | null;
};

type WindrowOption = {
  id: number;
  row_id: string;
  name: string;
};

type OpsTab = "temperatures" | "turns" | "materials" | "operators";

const COMPLIANCE_MIN_F = 131;

function compareValues(a: string | number, b: string | number) {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { sensitivity: "base" });
}

function toDayKey(dateString: string) {
  return dateString.slice(0, 10);
}

function formatDateTime(dateString: string | null) {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function groupRows<T>(
  rows: T[],
  groupBy: string,
  getKey: (row: T, group: string) => string,
  formatLabel: (key: string, group: string, count: number) => string,
) {
  if (groupBy === "none") {
    return [{ key: "all", label: `All Records (${rows.length})`, rows }];
  }

  const map = new Map<string, T[]>();
  for (const row of rows) {
    const key = getKey(row, groupBy);
    const group = map.get(key);
    if (group) {
      group.push(row);
    } else {
      map.set(key, [row]);
    }
  }

  return Array.from(map.entries()).map(([key, grouped]) => ({
    key,
    label: formatLabel(key, groupBy, grouped.length),
    rows: grouped,
  }));
}

export default function OperationsManagementPage() {
  const { token } = useAuth();

  const [activeTab, setActiveTab] = useState<OpsTab>("temperatures");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [temperatures, setTemperatures] = useState<TemperatureRecord[]>([]);
  const [turns, setTurns] = useState<TurnRecord[]>([]);
  const [materials, setMaterials] = useState<MaterialRecord[]>([]);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [windrows, setWindrows] = useState<WindrowOption[]>([]);

  const [search, setSearch] = useState("");
  const [windrowFilter, setWindrowFilter] = useState("all");
  const [sortBy, setSortBy] = useState("recorded_at");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [groupBy, setGroupBy] = useState("day");

  const [materialWindrowId, setMaterialWindrowId] = useState("");
  const [materialName, setMaterialName] = useState("");
  const [materialCubicYards, setMaterialCubicYards] = useState("");
  const [materialWeight, setMaterialWeight] = useState("");
  const [materialNotes, setMaterialNotes] = useState("");
  const [addingMaterial, setAddingMaterial] = useState(false);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [tempsRes, turnsRes, materialsRes, operatorsRes, windrowsRes] = await Promise.all([
        apiFetch("/api/temperatures", { headers: { Authorization: `Bearer ${token}` } }),
        apiFetch("/api/turns", { headers: { Authorization: `Bearer ${token}` } }),
        apiFetch("/api/materials", { headers: { Authorization: `Bearer ${token}` } }),
        apiFetch("/api/operations/operators", { headers: { Authorization: `Bearer ${token}` } }),
        apiFetch("/api/windrows/active", { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      const responses = [tempsRes, turnsRes, materialsRes, operatorsRes, windrowsRes];
      const failed = responses.find((res) => !res.ok);
      if (failed) {
        throw new Error(`Failed to load operations data (${failed.status})`);
      }

      const [tempsData, turnsData, materialsData, operatorsData, windrowsData] = await Promise.all([
        tempsRes.json(),
        turnsRes.json(),
        materialsRes.json(),
        operatorsRes.json(),
        windrowsRes.json(),
      ]);

      setTemperatures(tempsData.temperatures || []);
      setTurns(turnsData.turns || []);
      setMaterials(materialsData.materials || []);
      setOperators(operatorsData.operators || []);
      const activeWindrows = windrowsData.windrows || [];
      setWindrows(activeWindrows);
      if (!materialWindrowId && activeWindrows.length > 0) {
        setMaterialWindrowId(String(activeWindrows[0].id));
      }
    } catch (err: any) {
      setError(err?.message || "Failed to load operations data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [token]);

  useEffect(() => {
    setSearch("");
    setWindrowFilter("all");
    if (activeTab === "temperatures") {
      setSortBy("recorded_at");
      setSortDirection("desc");
      setGroupBy("day");
    } else if (activeTab === "turns") {
      setSortBy("turned_at");
      setSortDirection("desc");
      setGroupBy("day");
    } else if (activeTab === "materials") {
      setSortBy("added_at");
      setSortDirection("desc");
      setGroupBy("day");
    } else {
      setSortBy("name");
      setSortDirection("asc");
      setGroupBy("role");
    }
  }, [activeTab]);

  const preparedTemperatures = useMemo(() => {
    const searchLower = search.trim().toLowerCase();
    const filtered = temperatures.filter((record) => {
      if (windrowFilter !== "all" && String(record.windrow_id) !== windrowFilter) return false;
      if (!searchLower) return true;
      const haystack = [
        record.windrow_name,
        record.windrow_row_id,
        record.probe_location || "",
        record.recorded_by || "",
        record.notes || "",
        String(record.temperature),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(searchLower);
    });

    return [...filtered].sort((a, b) => {
      let left: string | number;
      let right: string | number;
      if (sortBy === "recorded_at") {
        left = new Date(a.recorded_at).getTime();
        right = new Date(b.recorded_at).getTime();
      } else if (sortBy === "temperature") {
        left = a.temperature;
        right = b.temperature;
      } else if (sortBy === "windrow_name") {
        left = `${a.windrow_name} ${a.windrow_row_id}`;
        right = `${b.windrow_name} ${b.windrow_row_id}`;
      } else {
        left = a.recorded_by || "";
        right = b.recorded_by || "";
      }
      const value = compareValues(left, right);
      return sortDirection === "asc" ? value : -value;
    });
  }, [temperatures, search, windrowFilter, sortBy, sortDirection]);

  const preparedTurns = useMemo(() => {
    const searchLower = search.trim().toLowerCase();
    const filtered = turns.filter((record) => {
      if (windrowFilter !== "all" && String(record.windrow_id) !== windrowFilter) return false;
      if (!searchLower) return true;
      const haystack = [
        record.windrow_name,
        record.windrow_row_id,
        record.equipment || "",
        record.operator || "",
        record.notes || "",
        String(record.turn_number),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(searchLower);
    });

    return [...filtered].sort((a, b) => {
      let left: string | number;
      let right: string | number;
      if (sortBy === "turned_at") {
        left = new Date(a.turned_at).getTime();
        right = new Date(b.turned_at).getTime();
      } else if (sortBy === "turn_number") {
        left = a.turn_number;
        right = b.turn_number;
      } else if (sortBy === "windrow_name") {
        left = `${a.windrow_name} ${a.windrow_row_id}`;
        right = `${b.windrow_name} ${b.windrow_row_id}`;
      } else if (sortBy === "equipment") {
        left = a.equipment || "";
        right = b.equipment || "";
      } else {
        left = a.operator || "";
        right = b.operator || "";
      }
      const value = compareValues(left, right);
      return sortDirection === "asc" ? value : -value;
    });
  }, [turns, search, windrowFilter, sortBy, sortDirection]);

  const preparedMaterials = useMemo(() => {
    const searchLower = search.trim().toLowerCase();
    const filtered = materials.filter((record) => {
      if (windrowFilter !== "all" && String(record.windrow_id) !== windrowFilter) return false;
      if (!searchLower) return true;
      const haystack = [
        record.windrow_name,
        record.windrow_row_id,
        record.material_name,
        record.notes || "",
        record.cubic_yards === null ? "" : String(record.cubic_yards),
        record.weight === null ? "" : String(record.weight),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(searchLower);
    });

    return [...filtered].sort((a, b) => {
      let left: string | number;
      let right: string | number;
      if (sortBy === "added_at") {
        left = new Date(a.added_at).getTime();
        right = new Date(b.added_at).getTime();
      } else if (sortBy === "material_name") {
        left = a.material_name;
        right = b.material_name;
      } else if (sortBy === "windrow_name") {
        left = `${a.windrow_name} ${a.windrow_row_id}`;
        right = `${b.windrow_name} ${b.windrow_row_id}`;
      } else if (sortBy === "cubic_yards") {
        left = a.cubic_yards || 0;
        right = b.cubic_yards || 0;
      } else {
        left = a.weight || 0;
        right = b.weight || 0;
      }
      const value = compareValues(left, right);
      return sortDirection === "asc" ? value : -value;
    });
  }, [materials, search, windrowFilter, sortBy, sortDirection]);

  const preparedOperators = useMemo(() => {
    const searchLower = search.trim().toLowerCase();
    const filtered = operators.filter((operator) => {
      if (!searchLower) return true;
      const haystack = [operator.name, operator.email, operator.role].join(" ").toLowerCase();
      return haystack.includes(searchLower);
    });

    return [...filtered].sort((a, b) => {
      let left: string | number;
      let right: string | number;
      if (sortBy === "name") {
        left = a.name;
        right = b.name;
      } else if (sortBy === "email") {
        left = a.email;
        right = b.email;
      } else if (sortBy === "last_active") {
        left = a.last_active ? new Date(a.last_active).getTime() : 0;
        right = b.last_active ? new Date(b.last_active).getTime() : 0;
      } else {
        left = a.role;
        right = b.role;
      }
      const value = compareValues(left, right);
      return sortDirection === "asc" ? value : -value;
    });
  }, [operators, search, sortBy, sortDirection]);

  const temperatureGroups = useMemo(
    () =>
      groupRows(
        preparedTemperatures,
        groupBy,
        (row, group) => {
          if (group === "windrow") return `${row.windrow_name} (${row.windrow_row_id})`;
          if (group === "operator") return row.recorded_by || "Unassigned";
          return toDayKey(row.recorded_at);
        },
        (key, group, count) => {
          if (group === "day") {
            return `${new Date(`${key}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} (${count})`;
          }
          return `${key} (${count})`;
        },
      ),
    [preparedTemperatures, groupBy],
  );

  const turnGroups = useMemo(
    () =>
      groupRows(
        preparedTurns,
        groupBy,
        (row, group) => {
          if (group === "windrow") return `${row.windrow_name} (${row.windrow_row_id})`;
          if (group === "operator") return row.operator || "Unassigned";
          if (group === "equipment") return row.equipment || "No equipment";
          return toDayKey(row.turned_at);
        },
        (key, group, count) => {
          if (group === "day") {
            return `${new Date(`${key}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} (${count})`;
          }
          return `${key} (${count})`;
        },
      ),
    [preparedTurns, groupBy],
  );

  const materialGroups = useMemo(
    () =>
      groupRows(
        preparedMaterials,
        groupBy,
        (row, group) => {
          if (group === "windrow") return `${row.windrow_name} (${row.windrow_row_id})`;
          if (group === "material") return row.material_name;
          return toDayKey(row.added_at);
        },
        (key, group, count) => {
          if (group === "day") {
            return `${new Date(`${key}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} (${count})`;
          }
          return `${key} (${count})`;
        },
      ),
    [preparedMaterials, groupBy],
  );

  const operatorGroups = useMemo(
    () =>
      groupRows(
        preparedOperators,
        groupBy,
        (row) => row.role,
        (key, _group, count) => `${key} (${count})`,
      ),
    [preparedOperators, groupBy],
  );

  const tabCounts = {
    temperatures: temperatures.length,
    turns: turns.length,
    materials: materials.length,
    operators: operators.length,
  };

  const sortOptions: Record<OpsTab, Array<{ value: string; label: string }>> = {
    temperatures: [
      { value: "recorded_at", label: "Date" },
      { value: "temperature", label: "Temperature" },
      { value: "windrow_name", label: "Windrow" },
      { value: "recorded_by", label: "Operator" },
    ],
    turns: [
      { value: "turned_at", label: "Date" },
      { value: "turn_number", label: "Turn #" },
      { value: "windrow_name", label: "Windrow" },
      { value: "operator", label: "Operator" },
      { value: "equipment", label: "Equipment" },
    ],
    materials: [
      { value: "added_at", label: "Date" },
      { value: "material_name", label: "Material" },
      { value: "windrow_name", label: "Windrow" },
      { value: "cubic_yards", label: "Cubic Yards" },
      { value: "weight", label: "Weight" },
    ],
    operators: [
      { value: "name", label: "Name" },
      { value: "email", label: "Email" },
      { value: "role", label: "Role" },
      { value: "last_active", label: "Last Active" },
    ],
  };

  const groupOptions: Record<OpsTab, Array<{ value: string; label: string }>> = {
    temperatures: [
      { value: "none", label: "No grouping" },
      { value: "day", label: "Day" },
      { value: "windrow", label: "Windrow" },
      { value: "operator", label: "Operator" },
    ],
    turns: [
      { value: "none", label: "No grouping" },
      { value: "day", label: "Day" },
      { value: "windrow", label: "Windrow" },
      { value: "operator", label: "Operator" },
      { value: "equipment", label: "Equipment" },
    ],
    materials: [
      { value: "none", label: "No grouping" },
      { value: "day", label: "Day" },
      { value: "windrow", label: "Windrow" },
      { value: "material", label: "Material" },
    ],
    operators: [
      { value: "none", label: "No grouping" },
      { value: "role", label: "Role" },
    ],
  };

  const matchingCount =
    activeTab === "temperatures"
      ? preparedTemperatures.length
      : activeTab === "turns"
      ? preparedTurns.length
      : activeTab === "materials"
      ? preparedMaterials.length
      : preparedOperators.length;

  const handleAddMaterial = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!materialWindrowId || !materialName.trim()) return;

    setAddingMaterial(true);
    setError(null);

    try {
      const res = await apiFetch(`/api/windrows/${materialWindrowId}/materials`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          materialName: materialName.trim(),
          cubicYards: materialCubicYards ? Number(materialCubicYards) : null,
          weight: materialWeight ? Number(materialWeight) : null,
          notes: materialNotes.trim() ? materialNotes.trim() : null,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Failed to add material (${res.status})`);
      }

      setMaterialName("");
      setMaterialCubicYards("");
      setMaterialWeight("");
      setMaterialNotes("");
      await loadData();
    } catch (err: any) {
      setError(err?.message || "Failed to add material");
    } finally {
      setAddingMaterial(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div>
          <h1 className="heading-display text-3xl text-foreground">Operations Data Center</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manager/admin tables with filter, sort, and grouping controls.
          </p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/app/log-temp"
            className="inline-flex items-center gap-2 px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-semibold"
          >
            <Thermometer size={14} />
            Temperature Table
          </Link>
          <Link
            to="/app/log-turn"
            className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold"
          >
            <RotateCcw size={14} />
            Turn Table
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="label-caps text-muted-foreground">Temperature Records</p>
          <p className="stat-value text-3xl text-foreground mt-2">{temperatures.length}</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="label-caps text-muted-foreground">Below 131°F</p>
          <p className="stat-value text-3xl text-red-600 dark:text-red-400 mt-2">
            {temperatures.filter((r) => r.temperature < COMPLIANCE_MIN_F).length}
          </p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="label-caps text-muted-foreground">Turn Records</p>
          <p className="stat-value text-3xl text-foreground mt-2">{turns.length}</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="label-caps text-muted-foreground">Material Records</p>
          <p className="stat-value text-3xl text-foreground mt-2">{materials.length}</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="label-caps text-muted-foreground">Operators</p>
          <p className="stat-value text-3xl text-foreground mt-2">{operators.length}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {[
          { key: "temperatures" as const, label: "Temperatures", icon: Thermometer },
          { key: "turns" as const, label: "Turns", icon: RotateCcw },
          { key: "materials" as const, label: "Materials", icon: Package },
          { key: "operators" as const, label: "Operators", icon: Users },
        ].map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                isActive
                  ? "bg-forest-600 text-white"
                  : "bg-secondary text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon size={14} />
              {tab.label}
              <span className={`text-xs ${isActive ? "text-white/70" : "text-muted-foreground"}`}>
                {tabCounts[tab.key]}
              </span>
            </button>
          );
        })}
      </div>

      <div className="bg-card border border-border rounded-xl p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
          <label className="lg:col-span-2 flex items-center gap-2 px-3 bg-background border border-border rounded-lg">
            <Search size={15} className="text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search records..."
              className="w-full py-2 text-sm bg-transparent outline-none"
            />
          </label>

          {activeTab !== "operators" ? (
            <select
              value={windrowFilter}
              onChange={(e) => setWindrowFilter(e.target.value)}
              className="px-3 py-2 bg-background border border-border rounded-lg text-sm"
            >
              <option value="all">All windrows</option>
              {windrows.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name} ({w.row_id})
                </option>
              ))}
            </select>
          ) : (
            <div className="px-3 py-2 bg-secondary border border-border rounded-lg text-sm text-muted-foreground">
              No windrow filter for operators
            </div>
          )}

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm"
          >
            {sortOptions[activeTab].map((option) => (
              <option key={option.value} value={option.value}>
                Sort by {option.label}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={() => setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"))}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm inline-flex items-center justify-center gap-1"
          >
            <ArrowUpDown size={14} />
            {sortDirection === "asc" ? "Ascending" : "Descending"}
          </button>

          <div className="inline-flex items-center gap-2">
            <FolderTree size={14} className="text-muted-foreground" />
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              className="px-3 py-2 bg-background border border-border rounded-lg text-sm w-full"
            >
              {groupOptions[activeTab].map((option) => (
                <option key={option.value} value={option.value}>
                  Group by {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
          <Filter size={12} />
          {matchingCount} matching records
        </div>
      </div>

      {activeTab === "materials" && (
        <form onSubmit={handleAddMaterial} className="bg-card border border-border rounded-xl p-5 space-y-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Plus size={16} className="text-forest-600" />
            Add Material Record
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
            <select
              value={materialWindrowId}
              onChange={(e) => setMaterialWindrowId(e.target.value)}
              className="lg:col-span-2 px-3 py-2.5 bg-background border border-border rounded-lg text-sm"
              required
            >
              <option value="">Select windrow</option>
              {windrows.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name} ({w.row_id})
                </option>
              ))}
            </select>

            <input
              type="text"
              value={materialName}
              onChange={(e) => setMaterialName(e.target.value)}
              placeholder="Material name"
              className="px-3 py-2.5 bg-background border border-border rounded-lg text-sm"
              required
            />

            <input
              type="number"
              min="0"
              step="0.1"
              value={materialCubicYards}
              onChange={(e) => setMaterialCubicYards(e.target.value)}
              placeholder="Cubic yards"
              className="px-3 py-2.5 bg-background border border-border rounded-lg text-sm"
            />

            <input
              type="number"
              min="0"
              step="0.1"
              value={materialWeight}
              onChange={(e) => setMaterialWeight(e.target.value)}
              placeholder="Weight"
              className="px-3 py-2.5 bg-background border border-border rounded-lg text-sm"
            />

            <button
              type="submit"
              disabled={addingMaterial || !materialWindrowId || !materialName.trim()}
              className="px-4 py-2.5 bg-forest-600 hover:bg-forest-700 disabled:opacity-50 text-white rounded-lg text-sm font-semibold"
            >
              {addingMaterial ? "Saving..." : "Save"}
            </button>
          </div>

          <input
            type="text"
            value={materialNotes}
            onChange={(e) => setMaterialNotes(e.target.value)}
            placeholder="Notes (optional)"
            className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm"
          />
        </form>
      )}

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-muted-foreground">Loading data...</div>
        ) : error ? (
          <div className="p-12 text-center text-red-600 dark:text-red-400">{error}</div>
        ) : activeTab === "temperatures" ? (
          temperatureGroups.length === 0 || preparedTemperatures.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">No temperature records found.</div>
          ) : (
            <div className="divide-y divide-border">
              {temperatureGroups.map((group) => (
                <section key={group.key} className="p-4">
                  {groupBy !== "none" && <h3 className="text-sm font-semibold text-foreground mb-3">{group.label}</h3>}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                          <th className="px-3 py-2.5">Recorded At</th>
                          <th className="px-3 py-2.5">Windrow</th>
                          <th className="px-3 py-2.5">Temperature</th>
                          <th className="px-3 py-2.5">Probe</th>
                          <th className="px-3 py-2.5">Operator</th>
                          <th className="px-3 py-2.5">Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.rows.map((record) => {
                          const below = record.temperature < COMPLIANCE_MIN_F;
                          return (
                            <tr key={record.id} className={`border-b border-border/60 last:border-0 ${below ? "bg-red-50/60 dark:bg-red-950/20" : ""}`}>
                              <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{formatDateTime(record.recorded_at)}</td>
                              <td className="px-3 py-2.5">
                                <p className="font-medium text-foreground">{record.windrow_name}</p>
                                <p className="text-xs text-muted-foreground font-mono">{record.windrow_row_id}</p>
                              </td>
                              <td className="px-3 py-2.5 whitespace-nowrap">
                                <span className={`font-semibold ${below ? "text-red-600 dark:text-red-400" : "text-foreground"}`}>
                                  {record.temperature}°F
                                </span>
                              </td>
                              <td className="px-3 py-2.5 text-muted-foreground capitalize">{record.probe_location || "-"}</td>
                              <td className="px-3 py-2.5 text-muted-foreground">{record.recorded_by || "-"}</td>
                              <td className="px-3 py-2.5 text-muted-foreground max-w-xs truncate">{record.notes || "-"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </section>
              ))}
            </div>
          )
        ) : activeTab === "turns" ? (
          turnGroups.length === 0 || preparedTurns.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">No turn records found.</div>
          ) : (
            <div className="divide-y divide-border">
              {turnGroups.map((group) => (
                <section key={group.key} className="p-4">
                  {groupBy !== "none" && <h3 className="text-sm font-semibold text-foreground mb-3">{group.label}</h3>}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                          <th className="px-3 py-2.5">Turned At</th>
                          <th className="px-3 py-2.5">Windrow</th>
                          <th className="px-3 py-2.5">Turn #</th>
                          <th className="px-3 py-2.5">Equipment</th>
                          <th className="px-3 py-2.5">Water</th>
                          <th className="px-3 py-2.5">Operator</th>
                          <th className="px-3 py-2.5">Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.rows.map((record) => (
                          <tr key={record.id} className="border-b border-border/60 last:border-0">
                            <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{formatDateTime(record.turned_at)}</td>
                            <td className="px-3 py-2.5">
                              <p className="font-medium text-foreground">{record.windrow_name}</p>
                              <p className="text-xs text-muted-foreground font-mono">{record.windrow_row_id}</p>
                            </td>
                            <td className="px-3 py-2.5 text-foreground font-semibold">#{record.turn_number}</td>
                            <td className="px-3 py-2.5 text-muted-foreground">{record.equipment || "-"}</td>
                            <td className="px-3 py-2.5">
                              {record.water_added ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                                  <Droplets size={11} /> Yes
                                </span>
                              ) : (
                                <span className="text-muted-foreground">No</span>
                              )}
                            </td>
                            <td className="px-3 py-2.5 text-muted-foreground">{record.operator || "-"}</td>
                            <td className="px-3 py-2.5 text-muted-foreground max-w-xs truncate">{record.notes || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              ))}
            </div>
          )
        ) : activeTab === "materials" ? (
          materialGroups.length === 0 || preparedMaterials.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">No material records found.</div>
          ) : (
            <div className="divide-y divide-border">
              {materialGroups.map((group) => (
                <section key={group.key} className="p-4">
                  {groupBy !== "none" && <h3 className="text-sm font-semibold text-foreground mb-3">{group.label}</h3>}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                          <th className="px-3 py-2.5">Added At</th>
                          <th className="px-3 py-2.5">Windrow</th>
                          <th className="px-3 py-2.5">Material</th>
                          <th className="px-3 py-2.5">Cubic Yards</th>
                          <th className="px-3 py-2.5">Weight</th>
                          <th className="px-3 py-2.5">Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.rows.map((record) => (
                          <tr key={record.id} className="border-b border-border/60 last:border-0">
                            <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{formatDateTime(record.added_at)}</td>
                            <td className="px-3 py-2.5">
                              <p className="font-medium text-foreground">{record.windrow_name}</p>
                              <p className="text-xs text-muted-foreground font-mono">{record.windrow_row_id}</p>
                            </td>
                            <td className="px-3 py-2.5 text-foreground">{record.material_name}</td>
                            <td className="px-3 py-2.5 text-muted-foreground">{record.cubic_yards ?? "-"}</td>
                            <td className="px-3 py-2.5 text-muted-foreground">{record.weight ?? "-"}</td>
                            <td className="px-3 py-2.5 text-muted-foreground max-w-xs truncate">{record.notes || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              ))}
            </div>
          )
        ) : operatorGroups.length === 0 || preparedOperators.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">No operators found.</div>
        ) : (
          <div className="divide-y divide-border">
            {operatorGroups.map((group) => (
              <section key={group.key} className="p-4">
                {groupBy !== "none" && <h3 className="text-sm font-semibold text-foreground mb-3">{group.label}</h3>}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                        <th className="px-3 py-2.5">Name</th>
                        <th className="px-3 py-2.5">Email</th>
                        <th className="px-3 py-2.5">Role</th>
                        <th className="px-3 py-2.5">Last Active</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.rows.map((operator) => (
                        <tr key={operator.id} className="border-b border-border/60 last:border-0">
                          <td className="px-3 py-2.5 text-foreground font-medium">{operator.name}</td>
                          <td className="px-3 py-2.5 text-muted-foreground">{operator.email}</td>
                          <td className="px-3 py-2.5 text-muted-foreground capitalize">{operator.role}</td>
                          <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">
                            {formatDateTime(operator.last_active)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
